import {
  Notification,
  NotificationCampaign,
  SendNotificationRequest,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  PaginatedNotifications,
  PaginatedNotificationLogs,
  NotificationEventSource,
  NotificationLogStatusFilter,
} from 'src/types/notification';
import { endpoints, axiosInstance } from 'src/utils/axios';

// ----------------------------------------------------------------------

class NotificationRepository {
  /**
   * GET /Notification
   * Retorna todas as notificações (endpoint de teste/admin)
   */
  static async getAll(params?: { search?: string }): Promise<Notification[]> {
    const response = await axiosInstance.get(endpoints.notifications.campaign.getAll, {
      params: {
        ...(params?.search ? { search: params.search } : {}),
      },
    });
    return response.data;
  }

  /**
   * POST /notification/send
   * Envia notificação push para um usuário específico
   */
  static async sendNotification(data: SendNotificationRequest): Promise<{ user_id: string; topic: string }> {
    const response = await axiosInstance.post(endpoints.notifications.send, data);
    return response.data;
  }

  /**
   * GET /notification/user
   * Lista notificações do usuário autenticado com paginação
   */
  static async getUserNotifications(params: {
    page?: number;
    perPage?: number;
    orderBy?: string;
    order?: 'asc' | 'desc';
  }): Promise<PaginatedNotifications> {
    const response = await axiosInstance.get(endpoints.notifications.getUserNotifications, {
      params: {
        page: params.page || 1,
        perPage: params.perPage || 10,
        orderBy: params.orderBy || 'createdAt',
        order: params.order || 'desc',
      },
    });
    return response.data;
  }

  /**
   * PUT /notification/:notification_id/view
   * Marca uma notificação específica como visualizada
   */
  static async markAsViewed(notificationId: string): Promise<{ notification_id: string }> {
    const response = await axiosInstance.put(`${endpoints.notifications.markAsViewed}/${notificationId}/view`);
    return response.data;
  }

  /**
   * GET /notification/has-unread
   * Verifica se o usuário possui notificações não lidas
   */
  static async hasUnread(): Promise<boolean> {
    const response = await axiosInstance.get(endpoints.notifications.hasUnread);
    return response.data;
  }

  /**
   * POST /notification/campaign
   * Cria e dispara campanha de notificação
   *
   * Payload:
   * {
   *   "title": "Título da notificação" (obrigatório),
   *   "message": "Conteúdo da mensagem" (obrigatório),
   *   "segmentId": "id-do-segmento" (opcional - para enviar ao grupo),
   *   "userIds": ["id1", "id2"] (opcional - para enviar a usuários específicos),
   *   "scheduledAt": "2024-01-01T10:00:00Z" (opcional - para agendar o envio)
   * }
   *
   * Nota: Use segmentId OU userIds, não ambos
   */
  static async createCampaign(data: CreateCampaignRequest): Promise<{ campaignId: string; message: string }> {
    const response = await axiosInstance.post(endpoints.notifications.campaign.create, data);
    return response.data;
  }

  /**
   * GET /notification/campaigns
   * Lista todas as campanhas de notificação criadas com paginação
   */
  static async getCampaigns(params?: {
    page?: number;
    perPage?: number;
    orderBy?: string;
    order?: 'asc' | 'desc';
    search?: string;
  }): Promise<{ items: NotificationCampaign[]; pagination: any }> {
    const response = await axiosInstance.get(endpoints.notifications.campaign.getAll, {
      params: {
        page: params?.page || 1,
        perPage: params?.perPage || 10,
        orderBy: params?.orderBy || 'createdAt',
        order: params?.order || 'desc',
        ...(params?.search ? { search: params.search } : {}),
      },
    });

    // Mapear resposta do backend para o formato esperado
    const items = response.data.items || response.data.campaigns || response.data || [];
    const paginationData = response.data.pagination || {};
    const perPage = params?.perPage || 10;
    const currentPage = Number(paginationData.currentPage ?? params?.page ?? 1);
    const totalCount = Number(paginationData.totalCount ?? response.data.totalCount ?? items.length);
    const totalPages = Number(paginationData.totalPages ?? Math.ceil(totalCount / perPage));

    const pagination = {
      totalCount,
      currentPage,
      totalPages,
      perPage: Number(paginationData.perPage ?? perPage),
      hasNext: Boolean(paginationData.hasNext ?? currentPage < totalPages),
      hasPrev: Boolean(paginationData.hasPrev ?? currentPage > 1),
    };

    return { items, pagination };
  }

  /**
   * GET /notification/campaign/:id
   * Busca uma campanha específica por ID
   */
  static async getCampaignById(id: string): Promise<NotificationCampaign> {
    const response = await axiosInstance.get(`${endpoints.notifications.campaign.getById}/${id}`);
    return response.data;
  }

  /**
   * PUT /notification/campaign/:id
   * Atualiza uma campanha de notificação existente
   */
  static async updateCampaign(id: string, data: UpdateCampaignRequest): Promise<NotificationCampaign> {
    const response = await axiosInstance.put(`${endpoints.notifications.campaign.update}/${id}`, data);
    return response.data;
  }

  /**
   * POST /notification/preview/estimate
   * Estima o alcance de uma campanha de notificação
   *
   * Payload:
   * {
   *   "segmentIds": ["segment_id_1", "segment_id_2", "segment_id_3"]
   * }
   *
   * Resposta:
   * {
   *   "matchedCount": 842
   * }
   */
  static async estimateCampaignReach(segmentIds: string[]): Promise<{ matchedCount: number }> {
    const response = await axiosInstance.post(endpoints.notifications.campaign.estimate, { segmentIds });
    return response.data;
  }

  /**
   * DELETE /notification/campaign/:id
   * Deleta uma campanha de notificação
   */
  static async deleteCampaign(id: string): Promise<void> {
    await axiosInstance.delete(`${endpoints.notifications.campaign.delete}/${id}`);
  }

  /**
   * POST /notification/send
   * Envia notificação individual para um usuário específico
   *
   * Payload:
   * {
   *   "userId": "string" (obrigatório),
   *   "title": "string" (obrigatório),
   *   "body": "string" (obrigatório),
   *   "type": "establishment" | "benefit" | "highlight" | "keepUp" | "mission" | "invoice" | "none" (obrigatório),
   *   "targetId": "string" (opcional),
   *   "imageUrl": "string" (opcional),
   *   "benefitType": "string" (opcional - preenchido automaticamente),
   *   "establishmentType": "string" (opcional - preenchido automaticamente)
   * }
   */
  static async sendIndividualNotification(data: SendNotificationRequest): Promise<{ message_id: string }> {
    const response = await axiosInstance.post(endpoints.notifications.send, data);
    return response.data;
  }

  /**
   * GET /notification/all
   * Lista todos os logs de notificações enviadas (Admin)
   *
   * Parâmetros de filtro:
   * - page: número da página (padrão: 1)
   * - perPage: itens por página (padrão: 10, máximo: 50)
   * - orderBy: campo para ordenação (padrão: createdAt)
   * - order: asc ou desc (padrão: desc)
   * - eventSource: filtrar por origem (individual, campaign ou proximity)
   * - onlySent: filtrar apenas notificações confirmadamente enviadas (padrão: true)
   * - title: filtrar por título
   * - status: filtrar por status de visualização (viewed ou notViewed)
   */
  static async getLogs(params?: {
    page?: number;
    perPage?: number;
    orderBy?: string;
    order?: 'asc' | 'desc';
    eventSource?: NotificationEventSource;
    onlySent?: boolean;
    title?: string;
    status?: NotificationLogStatusFilter;
  }): Promise<PaginatedNotificationLogs> {
    const response = await axiosInstance.get(endpoints.notifications.logs, {
      params: {
        page: params?.page || 1,
        perPage: params?.perPage || 10,
        orderBy: params?.orderBy || 'createdAt',
        order: params?.order || 'desc',
        ...(params?.eventSource && { eventSource: params.eventSource }),
        ...(params?.onlySent !== undefined && { onlySent: params.onlySent }),
        ...(params?.title && { title: params.title }),
        ...(params?.status && { status: params.status }),
      },
    });
    return response.data;
  }
}

export const notificationRepository = NotificationRepository;
